import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { toast } from 'material-react-toastify';
import Chart from 'react-apexcharts';

import { Box, Paper, Skeleton, Typography } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { ApexOptions } from 'apexcharts';
import { PaginatedResponse } from '../../../interfaces/PaginatedResponse';
import { SalesReport } from '../../../interfaces/Report/SalesReport/SalesReport';
import {
  getPartnerPlatformSalesReports,
  getSalesReport,
} from '../../../api/Report';

import { Period } from '../../../interfaces/Period';
import { dateMatches, formatLocalTime } from '../../../utils/Date';
import { formatCurrency } from '../../../utils/Currency';
import {
  PartnerPlatformSalesReport,
  PartnerPlatformSalesReports,
} from '../../../interfaces/Report/PartnerPlatformSalesReport/PartnerPlatformSalesReport';

type ChartEntry = {
  day: string;
  revenue: number;
};

export const RevenueWidget = () => {
  const startAt = DateTime.now().minus({ day: 7 }).startOf('day').toJSDate();

  const [report, setReport] = useState<PaginatedResponse<SalesReport>>({
    data: [],
    totalCount: 0,
  });
  const [partnerReport, setPartnerReport] = useState<
    PartnerPlatformSalesReports | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPartnerPlatformSalesReports(startAt)
      .then(setPartnerReport)
      .catch((err) => toast.error(err.message));

    getSalesReport({
      startAt,
      period: Period.DAY,
      pagination: { page: 0, pageSize: 10 },
    })
      .then((r) => setReport(r))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  const mapReportToEntry = useCallback(
    (reportToMap: PartnerPlatformSalesReport[]) => {
      const result: ChartEntry[] = [];
      let day = startAt;
      const now = new Date();

      while (day < now) {
        const dayDt = DateTime.fromJSDate(day);

        const r = reportToMap.find((x) =>
          dateMatches(dayDt.toJSDate(), x.createdAt),
        );

        result.push({
          day: formatLocalTime(day, 'dd.MM'),
          revenue: r?.total ?? 0,
        });

        day = dayDt.plus({ day: 1 }).toJSDate();
      }

      return result;
    },
    [startAt],
  );

  const series = useMemo(() => {
    let result: ApexAxisChartSeries = [
      {
        name: 'Web Sitesi',
        data: mapReportToEntry(report.data).map((x) => x.revenue),
        color: '#2E93FA',
      },
    ];

    if (partnerReport) {
      result = [
        ...result,
        {
          name: 'Trendyol',
          data: mapReportToEntry(partnerReport.trendyol).map((x) => x.revenue),
          color: '#FF6F00',
        },
        {
          name: 'Boyner',
          data: mapReportToEntry(partnerReport.boyner).map((x) => x.revenue),
          color: '#E60023',
        },
        {
          name: 'Hepsiburada',
          data: mapReportToEntry(partnerReport.hepsiburada).map(
            (x) => x.revenue,
          ),
          color: '#FFA94D',
        },
        {
          name: 'N11',
          data: mapReportToEntry(partnerReport.n11).map((x) => x.revenue),
          color: '#8B5CF6',
        },
        {
          name: 'Pazarama',
          data: mapReportToEntry(partnerReport.pazarama).map((x) => x.revenue),
          color: '#1E3A8A',
        },
      ];
    }

    return result;
  }, [report, partnerReport, mapReportToEntry]);

  const options: ApexOptions = {
    chart: {
      type: 'line',
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    yaxis: {
      labels: {
        formatter: (x) => formatCurrency(x),
      },
    },
    xaxis: {
      categories: mapReportToEntry(report.data).map((x) => x.day),
      title: {
        text: 'Tarih',
      },
    },
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 3,
        px: 2,
        gap: 1.5,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <AssessmentIcon />

        <Typography variant='h6'>Haftalık Gelir</Typography>
      </Box>

      {loading ? (
        <Skeleton variant='rectangular' height={200} width='100%' />
      ) : (
        <Chart
          options={options}
          series={series}
          type='area'
          width='100%'
          height={360}
        />
      )}
    </Paper>
  );
};
